import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, NgStyle } from '@angular/common';
import { TagComponentComponent } from '@app-components/common/tag-component/tag-component.component';
import { TAGS_COLORS } from '@app-shared/constants/constants';
import { TagContract } from '@app-types/api/tag';
import { TagClient } from '@app-services/api/clients/tag.client';
import { TagType } from '@app-types/enums/tags';
import { MatchError } from 'src/app/services/errors/error-matcher';

@Component({
  selector: 'app-email-search-tag-input',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatChipsModule,
    TranslateModule,
    AsyncPipe,
    ReactiveFormsModule,
    TagComponentComponent,
    NgStyle,
  ],
  templateUrl: './email-search-tag-input.component.html',
  styleUrls: ['./email-search-tag-input.component.scss'],
})
export class EmailSearchTagInputComponent implements OnInit {
  @Input({ required: true }) mailAccountId!: number;
  @Input({ required: true }) tagType!: TagType;
  @Input() selectedTags = new Set<TagContract>();
  @Input() initialLoadedTags: TagContract[] | null = null;
  @Output() tagsChange = new EventEmitter<TagContract[]>();

  tagCtrl = new UntypedFormControl();
  filteredTags: Observable<TagContract[]>;
  TAGS_COLORS = TAGS_COLORS;

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  private tagOptions$ = new BehaviorSubject<TagContract[]>([]);
  private loadedTags: TagContract[] = [];
  private destroyRef = inject(DestroyRef);

  constructor(
    private tagClient: TagClient,
    private matchError: MatchError
  ) {
    this.filteredTags = this.tagOptions$.asObservable();
  }

  ngOnInit(): void {
    if (this.initialLoadedTags !== null) {
      this.loadedTags = this.initialLoadedTags;
      this.refreshOptions();
    } else {
      void this.searchTags(null);
    }

    this.tagCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        if (value && typeof value !== 'string') {
          this.resetTagInput();
          return;
        }
        const search = typeof value === 'string' ? value.trim() || null : null;
        void this.searchTags(search);
      });
  }

  onInputFocus(): void {
    if (this.tagCtrl.value && typeof this.tagCtrl.value !== 'string') {
      this.resetTagInput();
    }

    this.refreshOptions();

    if (this.tagOptions$.value.length) {
      this.autocompleteTrigger?.openPanel();
      return;
    }

    void this.searchTags(null).then(() => {
      this.autocompleteTrigger?.openPanel();
    });
  }

  removeTag(tag: TagContract): void {
    const selected = Array.from(this.selectedTags).find(
      el =>
        el.tagId === tag.tagId || el.tag.toLowerCase() === tag.tag.toLowerCase()
    );
    if (selected) {
      this.selectedTags.delete(selected);
    }
    this.refreshOptions();
    this.emitTagsChange();
  }

  selectedTag(event: MatAutocompleteSelectedEvent): void {
    this.selectedTags.add(event.option.value);
    this.resetTagInput();
    this.refreshOptions();
    this.emitTagsChange();
  }

  private emitTagsChange(): void {
    this.tagsChange.emit([...this.selectedTags]);
  }

  private resetTagInput(): void {
    setTimeout(() => {
      this.tagInput.nativeElement.value = '';
      this.tagCtrl.setValue('', { emitEvent: false });
    });
  }

  private refreshOptions(): void {
    this.tagOptions$.next(this.excludeSelectedTags(this.loadedTags));
  }

  private excludeSelectedTags(tags: TagContract[]): TagContract[] {
    const selected = Array.from(this.selectedTags);
    return tags.filter(
      e =>
        !selected.some(
          el =>
            el.tagId === e.tagId || el.tag.toLowerCase() === e.tag.toLowerCase()
        )
    );
  }

  private async searchTags(search: string | null): Promise<void> {
    try {
      const response = await this.tagClient.searchTagsForSelect(
        this.mailAccountId,
        this.tagType,
        search
      );
      this.loadedTags = response.data;
      this.refreshOptions();
    } catch (e) {
      this.matchError.logError(e);
    }
  }
}
